import { type JSX, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { ImportModal } from '@/components/admin/import-modal';
import { RateFormModal } from '@/components/admin/rate-form-modal';
import { RoutingStrategyHelp, StrategyBadge } from '@/components/routing-strategy-help';
import { Badge, Button, Card, ConfirmModal, IconAction, Skeleton } from '@/components/ui';
import {
  usePreviewImportRates,
  useRunImportRates,
} from '@/hooks/useAdminImports';
import { useRateCard } from '@/hooks/useAdminRateCards';
import {
  rateValidityStatus,
  useDeleteRate,
  useRates,
  useUpsertRate,
  type Rate,
} from '@/hooks/useAdminRates';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api';
import { can } from '@/lib/permissions';
import { formatCurrency, formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Rate-card detail — lists + edits the rates inside a single rate card.
// Lives at /admin/rate-cards/:cardId. Per ADR-008 each rate carries the
// carrier (provider) directly; the dispatch path picks the longest-
// prefix-match within the card and applies the card's strategy tiebreak.
// ---------------------------------------------------------------------------

export default function AdminRatesPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const params = useParams<{ cardId: string }>();
  const cardId = Number(params.cardId);
  const navigate = useNavigate();

  const { user } = useAuth();
  const canEdit = can.useAdminModule(user?.role);
  const card = useRateCard(cardId);
  // Reseller-owned card: the carrier dimension is derived server-side and
  // never surfaced. Platform-owned cards (the wholesale catalog) keep the
  // explicit per-carrier rows the operator authored.
  //
  // Keyed on the session rather than card.data.owner_type so it's stable
  // from the first render — the two always agree (resolveCard only ever
  // hands a reseller its own cards), and deriving it from an async field
  // would render a Carrier header for one frame over rows that omit the
  // cell, misaligning the table.
  const derivedCarrier = user?.tenantType !== 'platform';
  const list = useRates(cardId);
  const upsert = useUpsertRate(cardId);
  const remove = useDeleteRate(cardId);

  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState<Rate | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Rate | null>(null);

  const previewImport = usePreviewImportRates(cardId);
  const runImport = useRunImportRates(cardId);

  const items = list.data?.items ?? [];

  const openNew = (): void => {
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (rate: Rate): void => {
    setEditing(rate);
    setShowForm(true);
  };
  const closeForm = (): void => {
    setShowForm(false);
    setEditing(null);
  };

  const handleDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync(pendingDelete.id);
    } catch {
      // refetch surfaces failures inline
    } finally {
      setPendingDelete(null);
    }
  };

  if (card.isError) {
    return (
      <div className="px-8 py-8 max-w-[1200px] mx-auto">
        <Card padding="lg">
          <div className="text-center py-10">
            <p className="text-[14px] text-text-2 font-medium">{t('ar_card_not_found')}</p>
            <Button className="mt-5" variant="secondary" onClick={() => navigate('/admin/rate-cards')}>
              <Icon.chevronLeft width="14" height="14" />
              {t('ar_back_to_cards')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <nav className="flex items-center gap-2 text-[12px] text-text-3 mb-4">
        <Icon.chevronLeft width="14" height="14" />
        <Link to="/admin/rate-cards" className="text-text-3 hover:text-text">
          {t('ar_back_to_cards')}
        </Link>
        <span className="text-text-4">/</span>
        <span className="text-text-2">{card.data?.name ?? '…'}</span>
      </nav>

      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
              {card.data?.name ?? t('ar_title')}
            </h1>
            {card.data && <StrategyBadge strategy={card.data.strategy} />}
            <RoutingStrategyHelp />
            {card.data?.is_default && <Badge variant="success">{t('arc_default_pill')}</Badge>}
          </div>
          {card.data && (
            <p className="text-[13px] text-text-3 mt-1.5">
              {t('ar_summary', {
                rates: card.data.rate_count.toLocaleString(lang),
                carriers: card.data.carrier_count.toLocaleString(lang),
                tenants: card.data.tenant_count.toLocaleString(lang),
              })}
            </p>
          )}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowImport(true)}>
              {t('ar_import')}
            </Button>
            <Button onClick={openNew}>
              <Icon.plus width="14" height="14" />
              {t('ar_new')}
            </Button>
          </div>
        )}
      </header>

      <Card padding="none">
        {list.isLoading ? (
          <div className="p-5 space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-[14px] text-text-2 font-medium">{t('ar_empty_title')}</p>
            <p className="text-[13px] text-text-3 mt-1 max-w-[460px] mx-auto">
              {t('ar_empty_sub')}
            </p>
            {canEdit && (
              <Button className="mt-5" onClick={openNew}>
                <Icon.plus width="14" height="14" />
                {t('ar_empty_cta')}
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-4 py-2.5 font-medium">{t('ar_col_prefix')}</th>
                  {!derivedCarrier && (
                    <th className="px-4 py-2.5 font-medium">{t('ar_col_carrier')}</th>
                  )}
                  <th className="px-4 py-2.5 font-medium">{t('ar_col_msg_type')}</th>
                  <th className="px-4 py-2.5 font-medium text-right">{t('ar_col_buy')}</th>
                  <th className="px-4 py-2.5 font-medium text-right">{t('ar_col_sell')}</th>
                  <th className="px-4 py-2.5 font-medium text-right">{t('ar_col_margin')}</th>
                  <th className="px-4 py-2.5 font-medium text-right">{t('ar_col_priority')}</th>
                  <th className="px-4 py-2.5 font-medium">{t('ar_col_validity')}</th>
                  <th className="px-4 py-2.5 font-medium">{t('ar_col_status')}</th>
                  <th className="px-4 py-2.5 font-medium text-right">{t('ar_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <RateRow
                    key={r.id}
                    rate={r}
                    lang={lang}
                    onEdit={() => openEdit(r)}
                    onDelete={() => setPendingDelete(r)}
                    canEdit={canEdit}
                    derivedCarrier={derivedCarrier}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showForm && (
        <RateFormModal
          editing={editing}
          derivedCarrier={derivedCarrier}
          onClose={closeForm}
          onSubmit={async (input) => {
            try {
              await upsert.mutateAsync(input);
              closeForm();
            } catch (err) {
              throw err instanceof ApiError ? err : new Error(t('ar_err_generic'));
            }
          }}
          submitting={upsert.isPending}
        />
      )}

      <ConfirmModal
        open={pendingDelete !== null}
        title={t('ar_delete_title')}
        description={
          pendingDelete
            ? derivedCarrier
              ? t('ar_delete_desc_reseller', { prefix: pendingDelete.prefix })
              : t('ar_delete_desc', {
                  prefix: pendingDelete.prefix,
                  carrier: pendingDelete.provider_name,
                })
            : ''
        }
        confirmLabel={t('ar_delete_action')}
        cancelLabel={t('ar_delete_cancel')}
        tone="danger"
        busy={remove.isPending}
        onConfirm={() => void handleDelete()}
        onClose={() => setPendingDelete(null)}
      />

      {showImport && (
        <ImportModal
          title={t('ar_import_title')}
          subtitle={t('ar_import_sub')}
          schemaHint={t('ar_import_schema_hint')}
          onClose={() => setShowImport(false)}
          onPreview={(file) => previewImport.mutateAsync(file)}
          onImport={(file) => runImport.mutateAsync(file)}
        />
      )}
    </div>
  );
}

interface RateRowProps {
  rate: Rate;
  lang: string;
  onEdit: () => void;
  onDelete: () => void;
  canEdit: boolean;
  derivedCarrier: boolean;
}
function RateRow({ rate, lang, onEdit, onDelete, canEdit, derivedCarrier }: RateRowProps): JSX.Element {
  const { t } = useTranslation('default');
  const margin = rate.margin;
  const marginPct = rate.buy_cost > 0 ? (margin / rate.buy_cost) * 100 : 0;
  const validity = formatValidity(rate.effective_from, rate.effective_to, lang, t);

  return (
    <tr className={cn('border-b border-line last:border-0 hover:bg-surface-2', !rate.is_active && 'opacity-60')}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="mono font-medium text-text">{rate.prefix === '*' ? '* (any)' : rate.prefix}</span>
        </div>
        {rate.name && <div className="text-[11px] text-text-3 mt-0.5">{rate.name}</div>}
      </td>
      {!derivedCarrier && <td className="px-4 py-3 text-text">{rate.provider_name}</td>}
      <td className="px-4 py-3">
        {rate.message_type ? (
          <Badge variant="neutral">{t(`ar_msg_${rate.message_type}`)}</Badge>
        ) : (
          <span className="text-[11px] text-text-4">{t('ar_msg_any')}</span>
        )}
      </td>
      <td className="px-4 py-3 num text-right text-text-2">{formatCurrency(rate.buy_cost, lang)}</td>
      <td className="px-4 py-3 num text-right text-text">{formatCurrency(rate.sell_price, lang)}</td>
      <td className="px-4 py-3 num text-right whitespace-nowrap">
        <span className={cn('font-medium', margin > 0 ? 'text-success' : margin < 0 ? 'text-danger' : 'text-text-3')}>
          {margin >= 0 ? '+' : ''}
          {formatCurrency(margin, lang)}
        </span>
        {rate.buy_cost > 0 && <span className="text-[11px] text-text-4 ml-1.5">({marginPct.toFixed(0)}%)</span>}
      </td>
      <td className="px-4 py-3 num text-right text-text-2">{rate.priority}</td>
      <td className="px-4 py-3 text-[12px] text-text-3 whitespace-nowrap">{validity}</td>
      <td className="px-4 py-3">
        {!rate.is_active ? (
          <Badge variant="neutral">{t('ar_status_inactive')}</Badge>
        ) : (() => {
          // Rates are now versioned by effective_from / effective_to
          // (PR 4 of billing sequence). The validity-by-time status is
          // separate from is_active — a row may be active=TRUE but
          // scheduled for the future, or already expired by an
          // effective_to. Surface both states so the operator sees at
          // a glance which rate is actually billing right now.
          const status = rateValidityStatus(rate);
          // Bundle keys live in i18n/{en,es}.json; no inline
          // defaultValue (drift risk between code and bundle).
          if (status === 'scheduled') {
            return <Badge variant="warn" title={t('ar_status_scheduled_help')}>{t('ar_status_scheduled')}</Badge>;
          }
          if (status === 'expired') {
            return <Badge variant="neutral" title={t('ar_status_expired_help')}>{t('ar_status_expired')}</Badge>;
          }
          return <Badge variant="success">{t('ar_status_active')}</Badge>;
        })()}
      </td>
      <td className="px-4 py-3 text-right">
        {canEdit ? (
          <div className="inline-flex gap-0.5">
            <IconAction icon={Icon.pencil} label={t('ar_edit')} onClick={onEdit} />
            <IconAction icon={Icon.trash} label={t('ar_delete')} onClick={onDelete} tone="danger" />
          </div>
        ) : (
          <span className="text-text-4 text-[12px]">—</span>
        )}
      </td>
    </tr>
  );
}

function formatValidity(
  effFrom: string,
  effTo: string | undefined,
  lang: string,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  const from = new Date(effFrom);
  const fromInPast = from <= new Date();
  if (!effTo) {
    return fromInPast ? t('ar_validity_active') : t('ar_validity_starts', { when: formatRelativeTime(effFrom, lang) });
  }
  const to = new Date(effTo);
  if (to <= new Date()) return t('ar_validity_expired');
  return t('ar_validity_until', { when: to.toLocaleDateString(lang, { month: 'short', day: 'numeric', year: 'numeric' }) });
}
